import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  DemoDataArea,
  GaiaEventType,
  startDemoDataRemoval,
  startDemoDataSeeding,
  TaskStatus,
  WebsocketSubscriptionTarget,
  type DemoDataSeedPayload,
  type DemoDataSeedStartResponse,
  type GaiaEvent,
} from '@/api';
import { WEBSOCKET_DISABLED_KEY } from '@/composables/useReviewWebSocket';
import { getWsClient } from '@/services/WebSocketService';

/** Title-case a snake_case enum value: 'contract_management' → 'Contract Management'. */
export function demoAreaLabel(area: DemoDataArea): string {
  return area
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Seedable areas, derived from the generated enum so a new backend area shows
 * up here without a frontend change. Listed in enum (backend) declaration order.
 */
export const DEMO_AREA_OPTIONS: { label: string; value: DemoDataArea }[] = Object.values(
  DemoDataArea
).map(value => ({ label: demoAreaLabel(value), value }));

function asSeedPayload(data: GaiaEvent['data']): DemoDataSeedPayload | null {
  return typeof data === 'object' && 'status' in data && 'completed_areas' in data ? data : null;
}

/** Whether the current run is seeding demo data or removing it. */
export type DemoRunMode = 'seed' | 'remove';

/**
 * Demo data seeding/removal runs as an async backend job: POST/DELETE kickoff →
 * `demo_data_seed_updated` progress events on the websocket (both operations
 * share the event contract and subscription target). This store owns the run
 * shown in the global bottom-right DemoSeedPanel, so the run (and its
 * websocket subscription) survives route changes — same architecture as
 * `downloadManagerStore` for folder-zip downloads, whose backend contract this
 * feature deliberately mirrors. One run at a time: both operations are
 * per-organisation and idempotent, so there is no value in queueing several.
 */
export const useDemoSeedStore = defineStore('demoSeed', () => {
  // The run IS the (self-describing) websocket payload — every event carries
  // `areas`, so the panel can render complete state from any single event.
  const job = ref<DemoDataSeedPayload | null>(null);
  const mode = ref<DemoRunMode>('seed');

  const isRunning = computed(() => job.value?.status === TaskStatus.RUNNING);

  // Non-reactive bookkeeping, released on the terminal event or dismiss.
  let uidDisposer: (() => void) | null = null;
  let handlerDisposer: (() => void) | null = null;

  function acquireSubscription(uid: string): void {
    const wsClient = getWsClient();
    handlerDisposer ??= wsClient.registerHandler(
      WebsocketSubscriptionTarget.DEMO_DATA_SEED,
      onSeedEvent
    );
    uidDisposer = wsClient.addSubscriptionUid(WebsocketSubscriptionTarget.DEMO_DATA_SEED, uid);
  }

  function releaseSubscription(): void {
    uidDisposer?.();
    uidDisposer = null;
    handlerDisposer?.();
    handlerDisposer = null;
  }

  // Completed runs clear themselves shortly after finishing (Drive-style);
  // failed runs stay until the user dismisses them.
  const AUTO_DISMISS_DELAY_MS = 3000;

  function scheduleAutoDismiss(uid: string): void {
    setTimeout(() => {
      if (job.value?.uid === uid && job.value.status === TaskStatus.DONE) {
        dismiss();
      }
    }, AUTO_DISMISS_DELAY_MS);
  }

  function onSeedEvent(event: GaiaEvent): void {
    if (event.gaia_event_type !== GaiaEventType.DEMO_DATA_SEED_UPDATED) {
      return;
    }
    const payload = asSeedPayload(event.data);
    if (!payload || payload.uid !== job.value?.uid || !isRunning.value) {
      return;
    }

    job.value = payload;

    if (payload.status === TaskStatus.DONE) {
      releaseSubscription();
      scheduleAutoDismiss(payload.uid);
    } else if (payload.status === TaskStatus.FAILED) {
      releaseSubscription();
    }
  }

  async function beginRun(
    kind: DemoRunMode,
    kickoff: () => Promise<{ data?: DemoDataSeedStartResponse; error?: unknown }>
  ): Promise<void> {
    if (localStorage.getItem(WEBSOCKET_DISABLED_KEY) === 'true') {
      throw new Error('Demo data operations need live updates, which are disabled in settings.');
    }
    if (isRunning.value) {
      return;
    }

    // Warm the socket before the kickoff so the subscribe frame below sends
    // immediately — the worker starts emitting events as soon as the request returns.
    await getWsClient().openConnection();

    const response = await kickoff();
    if (response.error || !response.data) {
      throw new Error(`Failed to start demo data ${kind === 'seed' ? 'seeding' : 'removal'}`);
    }

    // `areas` comes back in server-side execution order — use it (not what was
    // sent) as the denominator and step labels for progress.
    mode.value = kind;
    job.value = {
      uid: response.data.seed_uid,
      status: TaskStatus.RUNNING,
      areas: response.data.areas,
      completed_areas: [],
      error: null,
    };
    acquireSubscription(response.data.seed_uid);
  }

  /** Seed the given areas into the given organisation (platform-admin only). */
  async function startSeeding(areas: DemoDataArea[], organisationUid: string): Promise<void> {
    if (areas.length === 0) {
      return;
    }
    await beginRun('seed', () =>
      startDemoDataSeeding({ query: { organisation_uid: organisationUid }, body: { areas } })
    );
  }

  /** Remove ALL demo data (no area selection) from the given organisation (platform-admin only). */
  async function startRemoval(organisationUid: string): Promise<void> {
    await beginRun('remove', () =>
      startDemoDataRemoval({ query: { organisation_uid: organisationUid } })
    );
  }

  function dismiss(): void {
    // Dismissing an in-flight run just abandons it: there is no backend cancel,
    // and re-seeding later is safe because seeding is idempotent.
    releaseSubscription();
    job.value = null;
  }

  return {
    job,
    mode,
    isRunning,
    startSeeding,
    startRemoval,
    dismiss,
  };
});
